import { Fragment, useEffect, useMemo, type ReactNode } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchOrders, fetchOrderItemFlightDetails } from 'actions/OrderActions'
import fetchEsimPromoCodes from 'actions/loyalty/fetchEsimPromoCodes'
import useOrder from 'hooks/Order/useOrder'
import { fetchCruiseOfferSnapshot } from 'actions/CruiseActions'
import { getSortedOrdersByDeparture } from 'selectors/orderSelectors'
import isUpcoming from 'lib/order/isUpcoming'
import { isSpoofed } from 'selectors/isSpoofed'
import { haveOrdersBeenFetched, isOrderCancelled } from 'lib/order/orderUtils'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { EmptyArray, fillArray, take } from 'lib/array/arrayUtils'
import withMountGuard from 'hocs/withMountGuard'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getIsLoyaltyEsimEnabled from 'loyalty/selectors/getIsLoyaltyEsimEnabled'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import getNextUpcomingOrderWithRedeemableBenefits, { MAX_SCAN } from 'checkout/selectors/loyalty/getNextUpcomingOrderWithRedeemableBenefits'
import getUpcomingOrdersEligibleForLoungePassRedemption, { getPreQualifyingOrdersWithLoungeFilters } from 'checkout/selectors/loyalty/getUpcomingOrdersEligibleForLoungePassRedemption'
import determineOrderLoyaltyTransferExperience from 'checkout/selectors/loyalty/determineOrderLoyaltyTransferExperience'
import determineLoyaltyEligibleTransferOptions from 'checkout/selectors/loyalty/determineLoyaltyEligibleTransferOptions'
import { getOrderDisplayDepartureDate, getOrderDisplayEndDate } from 'loyalty/lib/esimOrderUtils'
import useLoyaltyLoungePassData from 'loyalty/hooks/useLoyaltyLoungePassData'
import useLoyaltyOrderViewTransferData from 'loyalty/hooks/useLoyaltyOrderViewTransferData'
import useTourV2Snapshot from 'hooks/TourV2/useTourV2Snapshot'
import useCruisePurchaseDetails from 'hooks/Cruise/useCruisePurchaseDetails'
import useFlightPurchaseDetails from 'hooks/Flights/useFlightPurchaseDetails'
import LoyaltyOrderViewBenefitsEsimItem from 'loyalty/components/LoyaltyOrderViewBenefitsEsimItem'
import LoyaltyOrderViewBenefitsLoungeItem from 'loyalty/components/LoyaltyOrderViewBenefitsLoungeItem'
import LoyaltyOrderViewBenefitsTransferItem from 'loyalty/components/LoyaltyOrderViewBenefitsTransferItem'
import LayoutContainer from 'components/Common/LayoutContainer'
import Sheet from 'components/utils/Sheet'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import Divider from 'LuxKit/Divider'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import AssistChipLoadingBox from 'LuxKit/Chips/AssistChipLoadingBox'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import UserAvatar from 'tripPlanner/Tripkit/Share/UserAvatar'
import { mediaQueryUp } from 'components/utils/breakpoint'

const Card = styled(Sheet)`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: stretch;
    gap: ${rem(24)};
  }
`

/** Full-width banner on mobile; fixed-width, full-height thumbnail on desktop. */
const ImageContainer = styled.div`
  width: 100%;
  height: ${rem(160)};
  flex-shrink: 0;
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    width: ${rem(180)};
    height: auto;
    align-self: stretch;
    border-radius: ${props => props.theme.borderRadius.L};
  }
`

const CarrierLogoGrid = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  gap: ${rem(12)};
  place-content: center;
  justify-items: center;
  align-items: center;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  grid-template-columns: 1fr;

  &.two-carriers {
    grid-template-columns: repeat(2, auto);
  }

  &.three-carriers {
    grid-template-columns: repeat(3, auto);
  }

  &.more-carriers {
    grid-template-columns: repeat(4, auto);

    ${mediaQueryUp.tablet} {
      grid-template-columns: repeat(2, auto);
    }
  }
`

const CarrierLogo = styled.img`
  width: ${rem(36)};

  &.carrier-logo-lg {
    width: ${rem(115)};
  }
`

const Content = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  padding: ${rem(16)};

  ${mediaQueryUp.tablet} {
    padding: 0;
  }
`

const Name = styled(BodyText)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

function formatDateRange(start: Date | undefined, end: Date | undefined): string {
  if (!start) return ''
  const startMoment = moment(start)
  if (!end) return startMoment.format('D MMM')
  const endMoment = moment(end)
  if (startMoment.isSame(endMoment, 'month')) return `${startMoment.format('D')} - ${endMoment.format('D MMM')}`
  return `${startMoment.format('D MMM')} - ${endMoment.format('D MMM')}`
}

interface CardLayoutProps {
  order: App.Order
  name: string
  imageId?: string
  image?: App.Image
  carrierLogos?: Array<string>
  rows: Array<ReactNode>
}

function CardLayout({ order, name, imageId, image, carrierLogos, rows }: CardLayoutProps) {
  const intl = useIntl()
  const dateRange = useMemo(
    () => formatDateRange(getOrderDisplayDepartureDate(order), getOrderDisplayEndDate(order)),
    [order],
  )
  const splitCarrierLogos = useMemo(() => take(carrierLogos ?? EmptyArray, 3), [carrierLogos])
  const moreCarriersCount = (carrierLogos?.length ?? 0) - 3

  return <Card border="light-foreground" borderRadius="L" padding={0} tabletPadding={24} shadow="flat-small">
    <ImageContainer>
      {imageId && <Image id={imageId} alt={name} fit="center" objectFit="cover" dpr={2} />}
      {!imageId && image && <Image image={image} alt={name} fit="center" objectFit="cover" dpr={2} />}
      {!imageId && !image && splitCarrierLogos.length > 0 && <CarrierLogoGrid
        className={clsx({
          'two-carriers': carrierLogos?.length === 2,
          'three-carriers': carrierLogos?.length === 3,
          'more-carriers': (carrierLogos?.length ?? 0) > 3,
        })}
      >
        {splitCarrierLogos.map((logo) => (
          <CarrierLogo
            alt=""
            className={clsx({ 'carrier-logo-lg': carrierLogos?.length === 1 })}
            key={logo}
            src={logo}
          />
        ))}
        {moreCarriersCount > 1 && (
          <UserAvatar
            variant="secondary"
            size="large"
            firstName="+"
            lastName={moreCarriersCount.toString()}
          />
        )}
      </CarrierLogoGrid>}
    </ImageContainer>
    <Content>
      <div>
        <Name variant="large" weight="bold">{name}</Name>
        <Group direction="horizontal" gap={4} verticalAlign="center" wrap="wrap">
          {!!dateRange && <BodyText variant="medium">{dateRange}{formatMiddleDotSeparator(intl)}</BodyText>}
          <TextLink size="medium" to={`/account/my-escapes/order/${order.id}`} weight="regular">
            <FormattedMessage id="RynhSMGTp5hERaxlAS9Q" defaultMessage="Manage booking" description="Link to manage an upcoming booking from the loyalty benefits card" />
          </TextLink>
        </Group>
      </div>
      <Rows>
        {rows.map((row, index) => <Fragment key={index}>
          <Divider kind="primary" />
          {row}
        </Fragment>)}
      </Rows>
    </Content>
  </Card>
}

function HotelOrderCard({ order, rows }: { order: App.Order, rows: Array<ReactNode> }) {
  const item = order.items[0]
  return <CardLayout order={order} name={item.offer.name} imageId={item.offer.image?.id} rows={rows} />
}

function BedbankOrderCard({ order, rows }: { order: App.Order, rows: Array<ReactNode> }) {
  const item = order.bedbankItems[0]
  return <CardLayout order={order} name={item.offer.name} image={item.offer.images?.[0]} rows={rows} />
}

function TourOrderCard({ order, rows }: { order: App.Order, rows: Array<ReactNode> }) {
  const item = order.tourItems[0]
  const [snapshot] = useTourV2Snapshot(item.reservationId, order.id)
  return <CardLayout order={order} name={snapshot?.tourName ?? item.tour.title} imageId={snapshot?.imageId ?? item.tour.images?.[0]?.id} rows={rows} />
}

function CruiseOrderCard({ order, rows }: { order: App.Order, rows: Array<ReactNode> }) {
  const { name, image } = useCruisePurchaseDetails(order, order.cruiseItems[0].bookingId)
  return <CardLayout order={order} name={name ?? ''} imageId={image} rows={rows} />
}

function FlightOrderCard({ order, rows }: { order: App.Order, rows: Array<ReactNode> }) {
  const { flightsName, carrierBrandMarks, carrierLogos } = useFlightPurchaseDetails(order)
  const availableCarrierImages = useMemo(
    () => {
      const carrierImages = carrierBrandMarks.length > 0 ? carrierBrandMarks : carrierLogos
      return carrierImages.filter((logo): logo is string => !!logo)
    },
    [carrierBrandMarks, carrierLogos],
  )
  return <CardLayout order={order} name={flightsName ?? ''} carrierLogos={availableCarrierImages} rows={rows} />
}

function UpcomingEscapesOrderCard({ order, rows }: { order: App.Order, rows: Array<ReactNode> }) {
  if (order.tourItems.length) return <TourOrderCard order={order} rows={rows} />
  if (order.items.length) return <HotelOrderCard order={order} rows={rows} />
  if (order.bedbankItems.length) return <BedbankOrderCard order={order} rows={rows} />
  if (order.cruiseItems.length) return <CruiseOrderCard order={order} rows={rows} />
  if (order.flightItems.length) return <FlightOrderCard order={order} rows={rows} />
  return null
}

interface CardProps {
  order: App.Order
  benefits: Array<'esim_package' | 'lounge_pass' | 'airport_transfer'>
}

function LoyaltyUpcomingEscapesBenefitsCard({ order, benefits }: CardProps) {
  // Full-order fetch so loyaltyDetails.benefitsRedeemed is populated (the list fetch omits it).
  useOrder(order.id)

  // Lounge: per-order availability filters + fetch (reuses the lounge-page pre-qualify logic).
  const loungeFilters = useAppSelector(getPreQualifyingOrdersWithLoungeFilters).filtersByOrderId[order.id] ?? EmptyArray
  useLoyaltyLoungePassData({ filters: loungeFilters })
  const loungeEligibleOrders = useAppSelector(getUpcomingOrdersEligibleForLoungePassRedemption)
  const loungeAvailable = useMemo(() => loungeEligibleOrders.some(o => o.id === order.id), [loungeEligibleOrders, order.id])

  // Transfer: per-order experience + options fetch + availability gate.
  useLoyaltyOrderViewTransferData({ order })
  const experienceLists = useAppSelector(state => state.experience.experienceLists)
  const experiences = useAppSelector(state => state.experience.experiences)
  const experienceTransferOptions = useAppSelector(state => state.experience.experienceTransferOptions)
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const transferExperience = useMemo(
    () => determineOrderLoyaltyTransferExperience(order, experienceLists, experiences),
    [order, experienceLists, experiences],
  )
  const transferOptions = useMemo(
    () => determineLoyaltyEligibleTransferOptions(order, transferExperience, experienceTransferOptions),
    [order, transferExperience, experienceTransferOptions],
  )
  const transferAvailable = transferOptions.canUseOneWay || transferOptions.canUseReturn || currentRegionCode === 'AU'

  const showEsim = benefits.includes('esim_package')
  const showLounge = benefits.includes('lounge_pass') && loungeAvailable
  const showTransfer = benefits.includes('airport_transfer') && transferAvailable

  if (!showEsim && !showLounge && !showTransfer) return null

  const rows = [
    showLounge && <LoyaltyOrderViewBenefitsLoungeItem key="lounge" order={order} layout="row" availabilityFilters={loungeFilters} />,
    showTransfer && <LoyaltyOrderViewBenefitsTransferItem key="transfer" order={order} layout="row" />,
    showEsim && <LoyaltyOrderViewBenefitsEsimItem key="esim" order={order} layout="row" />,
  ].filter(Boolean)

  return <UpcomingEscapesOrderCard order={order} rows={rows} />
}

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: ${rem(24)} minmax(0, 1fr) auto;
  align-items: center;
  gap: ${rem(12)};
`

const SkeletonImage = styled(LoadingBox)`
  width: 100%;
  height: 100%;
`

/**
 * Skeleton mirroring the card shape while orders load. `rows` is the account's post-purchase
 * benefit count (the upper bound for the real card), so it matches what will render.
 */
function LoadingSkeleton({ rows }: { rows: number }) {
  return <Card border="light-foreground" borderRadius="L" padding={0} tabletPadding={24} shadow="flat-small">
    <ImageContainer>
      <SkeletonImage />
    </ImageContainer>
    <Content>
      <div>
        <TextLoadingBox typography="body-large" width="80%" />
        <TextLoadingBox typography="body-medium" width="40%" />
      </div>
      <Rows>
        {fillArray(rows).map(index => <Fragment key={index}>
          <Divider kind="primary" />
          <SkeletonRow>
            <LoadingBox width={24} height={24} borderRadius="S" />
            <TextLoadingBox typography="body-medium" width="60%" />
            <AssistChipLoadingBox size="medium" width={110} />
          </SkeletonRow>
        </Fragment>)}
      </Rows>
    </Content>
  </Card>
}

function LoyaltyUpcomingEscapesBenefits() {
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const ordersFetched = useAppSelector(state => haveOrdersBeenFetched(state, 'upcoming'))
  const next = useAppSelector(getNextUpcomingOrderWithRedeemableBenefits)
  const benefits = useAppSelector(state => state.loyalty.account.data?.benefits)
  const esimEnabled = useAppSelector(getIsLoyaltyEsimEnabled)
  const accountRegion = useAppSelector(getLoyaltyAccountRegion)
  const esimBenefitListLoaded = useAppSelector(state => state.loyalty.esimPromoCodes.benefitListLoaded)
  const sortedOrders = useAppSelector(getSortedOrdersByDeparture)
  const spoofed = useAppSelector(isSpoofed)
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)

  useEffect(() => {
    if (isLoggedIn && !ordersFetched) {
      dispatch(fetchOrders())
    }
  }, [dispatch, isLoggedIn, ordersFetched])

  // Upper bound on rows the card can show — how many post-purchase benefits the account holds.
  // `undefined` while the account is still loading. Drives the skeleton row count + lets us skip
  // the skeleton entirely for members with none (the card would never render).
  const benefitPoolCount = useMemo(() => {
    if (!benefits) return undefined
    const has = (code: string) => benefits.some(b => b.code === code && (b.unlimited || !!b.remaining))
    return [has('lounge_pass'), has('airport_transfer'), esimEnabled && has('esim_package')].filter(Boolean).length
  }, [benefits, esimEnabled])
  const hasEsimInBenefitPool = benefitPoolCount !== undefined && esimEnabled && !!benefits?.some(
    b => b.code === 'esim_package' && (b.unlimited || !!b.remaining),
  )

  useEffect(() => {
    if (!accountRegion || !hasEsimInBenefitPool) return
    dispatch(fetchEsimPromoCodes())
  }, [dispatch, accountRegion, hasEsimInBenefitPool])

  const candidateOrders = useMemo(
    () => sortedOrders.data
      .filter(order =>
        !isOrderCancelled(order) &&
        isUpcoming(order, spoofed) &&
        order.isBookingOwner &&
        !!order.loyaltyDetails.tier,
      )
      .slice(0, MAX_SCAN),
    [sortedOrders, spoofed],
  )

  // `getNextUpcomingOrderWithRedeemableBenefits` classifies a trip as international from cruise
  // itinerary snapshots (state.cruise.cruiseOfferSnapshot) and flight details
  // (state.orders.flightDetails). Those are otherwise loaded lazily by the per-order cards — but a
  // card only mounts once an order is already selected, so a member whose soonest upcoming trip is a
  // cruise or flight bootstraps to "not international" → no eligible benefits → the section never
  // appears (until that data is incidentally loaded elsewhere, e.g. by visiting My Escapes).
  // Prefetch it up-front for those orders so eligibility resolves on first load. Both actions self-guard.
  useEffect(() => {
    if (!ordersFetched || !benefitPoolCount) return
    candidateOrders.forEach(order => {
      order.cruiseItems.forEach(item => dispatch(fetchCruiseOfferSnapshot(item.bookingId)))
      order.flightItems.forEach(item => dispatch(fetchOrderItemFlightDetails(order.id, item.id)))
    })
  }, [dispatch, ordersFetched, benefitPoolCount, candidateOrders])

  const cruiseSnapshots = useAppSelector(state => state.cruise.cruiseOfferSnapshot)
  const cruiseSnapshotErrors = useAppSelector(state => state.cruise.cruiseOfferSnapshotError)
  const flightDetails = useAppSelector(state => state.orders.flightDetails)
  const eligibilityDataPending = useMemo(
    () => candidateOrders.some(order =>
      order.cruiseItems.some(item => !cruiseSnapshots[item.bookingId] && !cruiseSnapshotErrors[item.bookingId]) ||
      order.flightItems.some(item => {
        const details = flightDetails[item.id]
        return !details || details.fetching
      }),
    ),
    [candidateOrders, cruiseSnapshots, cruiseSnapshotErrors, flightDetails],
  )

  const loading = !ordersFetched || eligibilityDataPending || (hasEsimInBenefitPool && !esimBenefitListLoaded)
  if (!isLoggedIn || benefitPoolCount === 0 || (!loading && !next)) return null

  // While orders load, the whole section is a skeleton (title + card + button) — these elements
  // only exist if the section does, so they shouldn't render as real chrome before we know.
  if (loading) {
    return <Sheet background="normal-background" padding="24 0 0" tabletPadding="48 0 0">
      <LayoutContainer variant="medium">
        <VerticalSpacer gap={16}>
          <TextLoadingBox typography="heading4" width="60%" />
          <LoadingSkeleton rows={benefitPoolCount ?? 2} />
          <LoadingBox width={168} height={40} borderRadius="M" />
        </VerticalSpacer>
      </LayoutContainer>
    </Sheet>
  }

  return <Sheet background="normal-background" padding="24 0 0" tabletPadding="48 0 0">
    <LayoutContainer variant="medium">
      <VerticalSpacer gap={16}>
        <Heading variant="heading4">
          {isInfiniteTier && <FormattedMessage
            id="nFjrE-C8_Ezx3PwQgOO_"
            defaultMessage="Enjoy Infinite benefits on your upcoming escapes"
            description="Heading for the upcoming escapes loyalty benefits section shown to Infinite tier members"
          />}
          {!isInfiniteTier && <FormattedMessage
            id="qTDrmxgNdJEnlEEMdyh9"
            defaultMessage="Enjoy Société benefits on your upcoming escapes"
            description="Heading for the upcoming escapes loyalty benefits section"
          />}
        </Heading>
        {next && <LoyaltyUpcomingEscapesBenefitsCard order={next.order} benefits={next.benefits} />}
        {next && next.eligibleCount > 1 && <div>
          <TextButton kind="secondary" variant="dark" to="/account/my-escapes" endIcon={<LineAngleRightIcon />}>
            <FormattedMessage id="fQojXCur1v1iZDo9abwr" defaultMessage="View all bookings" description="Button linking to the full bookings list from the loyalty benefits card" />
          </TextButton>
        </div>}
      </VerticalSpacer>
    </LayoutContainer>
  </Sheet>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(LoyaltyUpcomingEscapesBenefits)
